'use client';

import { useEffect, useRef } from 'react';
import 'keen-slider/keen-slider.min.css';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useGSAP } from '@gsap/react';
import initAnimation from './animations/data-problem-animation';
import { useLocale } from '@/components/providers/context/locale-context';
import { JContainer } from '../../ui/layout';

function DataProblem() {
    const { t } = useTranslation('data-problem');
    const locale = useLocale();

    const challengesList: { id: string; title: string; content: string }[] = t(
        'challengesList',
        {
            returnObjects: true,
        },
    ) as { id: string; title: string; content: string }[];

    const rootRef = useRef<HTMLDivElement>(null);
    const leftText = useRef<HTMLDivElement>(null);
    const rightTextContainer = useRef<HTMLDivElement>(null);
    const rightText = useRef<HTMLDivElement>(null);

    useEffect(() => {
        document.documentElement.style.setProperty(
            '--dynamic-bg-color',
            'rgb(255,255,255)',
        );
    }, [locale]);

    useGSAP(
        () => {
            return initAnimation(
                rootRef.current!,
                leftText.current!,
                rightTextContainer.current!,
                rightText.current!,
            );
        },
        { scope: rootRef },
    );

    return (
        <section
            ref={rootRef}
            id={'data-problem-container'}
            style={{
                backgroundColor: 'rgb(255,255,255)',
                height: 'var(--simulaix-vh, 100svh)',
            }}
            className="flex w-full flex-col items-center"
        >
            <JContainer
                id="data-problem-container"
                size="lg"
                padding="none"
                className="flex h-full flex-col justify-center"
            >
                <div
                    className={clsx(
                        'flex h-full w-full flex-col justify-between gap-[50px] px-[16px] pt-[80px] sm:flex-row sm:gap-0 sm:px-[40px] sm:pt-[160px]',
                    )}
                >
                    <div
                        ref={leftText}
                        className={clsx(
                            'j-h1 flex h-auto w-full flex-col text-left text-[#06060A] sm:h-full sm:w-3/5',
                        )}
                    >
                        <h2 className="hidden">
                            {t('challenges.part1')}
                            {t('challenges.part2')}
                            {t('challenges.part3')}
                            {t('challenges.part4')}
                            {t('challenges.part5')}
                        </h2>
                        <span className={clsx('w-full sm:w-4/5')}>
                            {t('challenges.part1')}
                        </span>

                        <span className="hidden sm:block">
                            {t('challenges.part2')}
                        </span>

                        <span className="block w-full sm:hidden sm:w-4/5">
                            {t('challenges.part2')}
                            {t('challenges.part3')}
                            <span
                                style={{
                                    background:
                                        'linear-gradient(90deg, #5C76FF 0%, #AD5CFF 93.24%)',
                                    WebkitTextFillColor: 'transparent',
                                    WebkitBackgroundClip: 'text',
                                    backgroundClip: 'text',
                                }}
                            >
                                {t('challenges.part4')}
                            </span>
                            {t('challenges.part5')}
                        </span>

                        <span className="hidden w-full sm:block sm:w-4/5">
                            {t('challenges.part3')}
                            <span
                                style={{
                                    background:
                                        'linear-gradient(90deg, #5C76FF 0%, #AD5CFF 93.24%)',
                                    WebkitTextFillColor: 'transparent',
                                    WebkitBackgroundClip: 'text',
                                    backgroundClip: 'text',
                                }}
                            >
                                {t('challenges.part4')}
                            </span>
                            {t('challenges.part5')}
                        </span>
                    </div>

                    <div
                        id="data-problem-question-list"
                        ref={rightTextContainer}
                        className={clsx(
                            'relative h-full w-full flex-col overflow-hidden sm:h-4/5 sm:w-2/5',
                        )}
                    >
                        <div
                            id="data-problems"
                            ref={rightText}
                            className="flex w-full flex-col gap-[80px] sm:gap-[120px]"
                        >
                            {challengesList.map((item, index) => (
                                <div
                                    id={'challenge-' + item.id}
                                    key={'challenge-' + item.id}
                                    className={clsx(
                                        'animated-padding flex flex-col gap-[16px] sm:gap-[32px]' /* 其他类名 */,
                                    )}
                                >
                                    <h3
                                        className={clsx(
                                            'j-t1 text-left text-[#0D0D10CC]',
                                        )}
                                    >
                                        {item.title}
                                    </h3>

                                    <div
                                        className={clsx(
                                            'j-t1 text-left text-[#0D0D1080]',
                                        )}
                                    >
                                        {item.content}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div
                            className="absolute bottom-0 left-0 right-0 h-1/4 w-full"
                            style={{
                                backgroundImage: `-webkit-linear-gradient(top, hsla(0,0%,100%,0), var(--dynamic-bg-color)`,
                            }}
                        ></div>
                        <div
                            className="absolute top-0 h-1/4 w-full"
                            style={{
                                backgroundImage: `-webkit-linear-gradient(top, var(--dynamic-bg-color), hsla(0,0%,100%,0))`,
                            }}
                        ></div>
                    </div>
                </div>
            </JContainer>
        </section>
    );
}

export default DataProblem;
